/**
 * Writing help: Claude rewrites a selection (or writes at the cursor) in a note.
 * The note goes along as context with the selection marked; the answer is the
 * replacement Markdown, shown as a word diff before the user accepts it.
 */
import type { AiModel, AiUsage, MessageParam } from './protocol';
import { AiError, callOnce, type Transport } from './chat';

export const SEL_START = '⟦';
export const SEL_END = '⟧';
/** Context beyond this many characters around the selection is left out. */
const CONTEXT_CHARS = 12_000;

export const WRITE_ACTIONS = [
  'improve',
  'shorter',
  'longer',
  'simpler',
  'professional',
  'friendly',
  'fix',
  'bullets',
  'translate-en',
  'translate-nl',
] as const;
export type WriteAction = (typeof WRITE_ACTIONS)[number];

export const INSERT_ACTIONS = ['continue', 'summary', 'actions'] as const;
export type InsertAction = (typeof INSERT_ACTIONS)[number];

const INSTRUCTIONS: Record<WriteAction | InsertAction, string> = {
  improve: 'Improve the writing: clearer and more fluent, same meaning and length.',
  shorter: 'Make it shorter and more to the point; keep the essentials.',
  longer: 'Expand it with more detail and explanation, in the same style. Do not invent facts.',
  simpler: 'Rewrite it in simpler, plain language.',
  professional: 'Rewrite it in a professional, businesslike tone.',
  friendly: 'Rewrite it in a warm, friendly tone.',
  fix: 'Fix spelling, grammar and punctuation only; change nothing else.',
  bullets: 'Turn it into a concise bullet list.',
  'translate-en': 'Translate it into English.',
  'translate-nl': 'Translate it into Dutch.',
  continue:
    'Continue writing from the cursor, in the style and language of the note (one or two paragraphs).',
  summary: 'Write a short summary of the whole note (a few bullet points).',
  actions:
    'Write a task list ("- [ ] ...") of the action items in the note that are not tasks yet.',
};

export const instructionFor = (action: WriteAction | InsertAction) => INSTRUCTIONS[action];

/**
 * The note with the selection marked (⟦…⟧, or ⟦⟧ for the cursor), trimmed to
 * the part around the selection when it is long.
 */
export function markedNote(markdown: string, from: number, to: number): string {
  const marked = `${markdown.slice(0, from)}${SEL_START}${markdown.slice(from, to)}${SEL_END}${markdown.slice(to)}`;
  if (marked.length <= CONTEXT_CHARS * 2 + (to - from)) return marked;
  const start = Math.max(0, from - CONTEXT_CHARS);
  const end = Math.min(marked.length, to + 2 + CONTEXT_CHARS);
  return `${start > 0 ? '[…]\n' : ''}${marked.slice(start, end)}${end < marked.length ? '\n[…]' : ''}`;
}

export function writeMessage(opts: {
  title: string;
  /** The note as Markdown with the selection marked (see markedNote). */
  note: string;
  instruction: string;
}): MessageParam {
  const title = opts.title.replace(/"/g, "'");
  return {
    role: 'user',
    content: `<page title="${title}">\n${opts.note}\n</page>\n\n<instruction>${opts.instruction.trim()}</instruction>`,
  };
}

/** Claude sometimes wraps the answer in a code fence or quotes anyway. */
export function cleanWriteResult(text: string): string {
  let t = text.trim();
  const fence = /^```(?:markdown|md)?\n([\s\S]*?)\n```$/.exec(t);
  if (fence) t = fence[1]!.trim();
  return t.replaceAll(SEL_START, '').replaceAll(SEL_END, '');
}

export async function runWrite(opts: {
  transport: Transport;
  model: AiModel;
  message: MessageParam;
  signal?: AbortSignal;
  onText?: (soFar: string) => void;
  onUsage?: (usage: AiUsage) => void;
}): Promise<string> {
  let text = '';
  const done = await callOnce(
    opts.transport,
    { mode: 'write', model: opts.model, messages: [opts.message] },
    opts.signal,
    {
      onText: (t) => {
        text += t;
        opts.onText?.(text);
      },
      onUsage: opts.onUsage,
    },
  );
  if (done.stop_reason === 'refusal') throw new AiError('refusal');
  return cleanWriteResult(text);
}

// ---- word diff -----------------------------------------------------------------

export type DiffPart = { kind: 'same' | 'added' | 'removed'; text: string };

/** Words and the whitespace after them, so joining the parts gives the text back. */
const tokens = (s: string) => s.match(/\S+\s*|\s+/g) ?? [];

/** Word-level diff (longest common subsequence). Large inputs: removed then added. */
export function diffWords(before: string, after: string): DiffPart[] {
  const a = tokens(before);
  const b = tokens(after);
  if (a.length * b.length > 4_000_000)
    return [
      { kind: 'removed', text: before },
      { kind: 'added', text: after },
    ];
  const key = (t: string) => t.trimEnd();
  // lcs[i][j] = length of the LCS of a[i:] and b[j:]
  const lcs = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i--)
    for (let j = b.length - 1; j >= 0; j--)
      lcs[i]![j] =
        key(a[i]!) === key(b[j]!)
          ? lcs[i + 1]![j + 1]! + 1
          : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
  const parts: DiffPart[] = [];
  const push = (kind: DiffPart['kind'], text: string) => {
    const last = parts[parts.length - 1];
    if (last?.kind === kind) last.text += text;
    else parts.push({ kind, text });
  };
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (key(a[i]!) === key(b[j]!)) {
      // Same word; differing whitespace after it counts as removed/added.
      const word = key(b[j]!);
      const wsA = a[i]!.slice(word.length);
      const wsB = b[j]!.slice(word.length);
      push('same', wsA === wsB ? b[j]! : word);
      if (wsA !== wsB) {
        if (wsA) push('removed', wsA);
        if (wsB) push('added', wsB);
      }
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) push('removed', a[i++]!);
    else push('added', b[j++]!);
  }
  while (i < a.length) push('removed', a[i++]!);
  while (j < b.length) push('added', b[j++]!);
  return parts;
}
